import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, HardDrive, Loader2 } from 'lucide-react';
import { exportsAPI } from '../../api';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Tooltip, TooltipProvider } from '../ui/Tooltip';

const POLL_MS = 12000;

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      return utf[1].trim();
    }
  }
  const m = /filename="([^"]+)"/i.exec(header);
  if (m?.[1]) return m[1];
  const m2 = /filename=([^;\s]+)/i.exec(header);
  return m2?.[1]?.replace(/["']/g, '') ?? null;
}

function isDownloadExpired(job) {
  if (job.status !== 'completed') return true;
  if (!job.downloadExpiresAt) return false;
  return new Date(job.downloadExpiresAt).getTime() <= Date.now();
}

function statusBadgeVariant(status) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'processing':
    case 'queued':
      return 'secondary';
    case 'expired':
      return 'outline';
    default:
      return 'outline';
  }
}

/** @param {{ toastSuccess: (t: string, d?: string) => void, toastError: (t: string, d?: string) => void }} props */
export default function BackupExportSettings({ toastSuccess, toastError }) {
  const [datasetMeta, setDatasetMeta] = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [fullExport, setFullExport] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const prevStatusByIdRef = useRef(new Map());
  const masterCheckboxRef = useRef(null);

  const allKeys = useMemo(() => datasetMeta.map((d) => d.key), [datasetMeta]);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const d of datasetMeta) {
      const g = d.group || 'Other';
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(d);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [datasetMeta]);

  const hasActiveJob = useMemo(
    () => jobs.some((j) => j.status === 'queued' || j.status === 'processing'),
    [jobs]
  );

  const refreshDatasets = useCallback(async () => {
    try {
      setDatasetsLoading(true);
      const res = await exportsAPI.getDatasets();
      const list = Array.isArray(res?.data) ? res.data : [];
      setDatasetMeta(list);
    } catch (e) {
      toastError('Could not load export options', e.message || 'Try again later.');
    } finally {
      setDatasetsLoading(false);
    }
  }, [toastError]);

  const refreshJobs = useCallback(async () => {
    try {
      const res = await exportsAPI.listJobs();
      const list = Array.isArray(res?.data) ? res.data : [];
      setJobs(list);

      const prev = prevStatusByIdRef.current;
      for (const job of list) {
        const old = prev.get(job.id);
        if (old && old !== 'completed' && job.status === 'completed') {
          toastSuccess('Export ready', 'Your backup ZIP is ready to download from the list below.');
        }
        if (old && old !== 'failed' && job.status === 'failed') {
          toastError(
            'Export failed',
            job.failureReason || 'Something went wrong while building your export.'
          );
        }
        prev.set(job.id, job.status);
      }
    } catch (e) {
      toastError('Could not load export history', e.message || 'Try again later.');
    } finally {
      setJobsLoading(false);
    }
  }, [toastSuccess, toastError]);

  useEffect(() => {
    refreshDatasets();
  }, [refreshDatasets]);

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const t = setInterval(() => {
      refreshJobs();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [hasActiveJob, refreshJobs]);

  const clearSelection = () => {
    setFullExport(false);
    setSelectedKeys(new Set());
  };

  const selectAllKeys = () => {
    setFullExport(true);
    setSelectedKeys(new Set(allKeys));
  };

  const toggleDatasetKey = (key) => {
    if (fullExport) {
      setFullExport(false);
      const next = new Set(allKeys);
      next.delete(key);
      setSelectedKeys(next);
      return;
    }
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const masterChecked = fullExport && allKeys.length > 0;
  const masterIndeterminate = !fullExport && selectedKeys.size > 0 && selectedKeys.size < allKeys.length;

  const toggleMaster = () => {
    if (masterChecked || (selectedKeys.size === allKeys.length && allKeys.length > 0)) {
      clearSelection();
    } else {
      selectAllKeys();
    }
  };

  useEffect(() => {
    const el = masterCheckboxRef.current;
    if (el) {
      el.indeterminate = masterIndeterminate;
    }
  }, [masterIndeterminate, masterChecked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasActiveJob) {
      toastError('Export already running', 'Wait for the current export to finish before starting another.');
      return;
    }
    const payload = fullExport
      ? { fullExport: true }
      : { datasets: [...selectedKeys] };
    if (!fullExport && selectedKeys.size === 0) {
      toastError('Nothing selected', 'Choose at least one dataset, or use full tenant export.');
      return;
    }
    setSubmitting(true);
    try {
      await exportsAPI.createJob(payload);
      toastSuccess('Export queued', 'We will prepare your ZIP. You can download it here when it is ready.');
      await refreshJobs();
    } catch (err) {
      const msg = err.message || 'Could not start export.';
      const status = err.status ?? err.response?.status;
      if (status === 409) {
        toastError('Export already in progress', msg);
      } else if (status === 429) {
        toastError('Export limit reached', msg);
      } else {
        toastError('Export failed to start', msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (job) => {
    setDownloadingId(job.id);
    try {
      const response = await exportsAPI.downloadZip(job.id);
      const blob = response.data;
      let name = job.fileName && /\.zip$/i.test(job.fileName) ? job.fileName : `export-${String(job.id).slice(0, 8)}.zip`;
      const fromHeader = parseFilenameFromContentDisposition(response.headers['content-disposition']);
      if (fromHeader) name = fromHeader;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toastSuccess('Download started', name);
    } catch (err) {
      toastError('Download failed', err.message || 'The file may have expired or been removed.');
    } finally {
      setDownloadingId(null);
    }
  };

  const rowCountsHint = (rowCounts) => {
    if (!rowCounts || typeof rowCounts !== 'object') return null;
    const parts = Object.entries(rowCounts)
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => `${k}: ${v}`);
    return parts.length ? parts.join(', ') : null;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-6 flex items-start gap-3">
          <HardDrive className="h-8 w-8 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
          <div>
            <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Backup / Export Data</h2>
            <p className="text-meta text-[var(--ds-text-secondary)] mt-1 max-w-3xl">
              Download a portable snapshot of your tenant data as CSV files inside a single ZIP. This supports data
              portability and offline analysis — it is not a full disaster-recovery backup of the platform. Uploaded
              files (e.g. attachments) are not included in this export.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-3">What to include</h3>
            {datasetsLoading ? (
              <div className="flex items-center gap-2 text-[var(--ds-text-muted)] text-sm py-8">
                <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden />
                Loading datasets…
              </div>
            ) : (
              <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel2)] p-4 space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllKeys}>
                    Select all
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={clearSelection}>
                    Clear all
                  </Button>
                </div>

                <label className="flex items-start gap-3 cursor-pointer rounded-[var(--ds-radius-sm)] p-2 hover:bg-[var(--ds-control-field-hover-bg)]">
                  <input
                    ref={masterCheckboxRef}
                    type="checkbox"
                    className="mt-1 rounded border-[var(--ds-border-subtle)]"
                    checked={masterChecked}
                    onChange={toggleMaster}
                  />
                  <span className="text-body text-[var(--ds-text-primary)]">
                    <span className="font-medium">Full tenant export</span>
                    <span className="block text-meta text-[var(--ds-text-muted)]">
                      Includes every dataset listed below (submitted as one full export).
                    </span>
                  </span>
                </label>

                <div className="space-y-4 border-t border-[var(--ds-border-subtle)] pt-4">
                  {grouped.map(([group, rows]) => (
                    <div key={group}>
                      <p className="text-micro font-semibold uppercase tracking-wide text-[var(--ds-text-muted)] mb-2">
                        {group}
                      </p>
                      <ul className="space-y-2">
                        {rows.map((d) => {
                          const checked = fullExport || selectedKeys.has(d.key);
                          return (
                            <li key={d.key}>
                              <label className="flex items-start gap-3 cursor-pointer rounded-[var(--ds-radius-sm)] p-2 hover:bg-[var(--ds-control-field-hover-bg)]">
                                <input
                                  type="checkbox"
                                  className="mt-1 rounded border-[var(--ds-border-subtle)]"
                                  checked={checked}
                                  onChange={() => toggleDatasetKey(d.key)}
                                />
                                <span>
                                  <span className="text-body font-medium text-[var(--ds-text-primary)]">{d.label}</span>
                                  {d.description ? (
                                    <span className="block text-meta text-[var(--ds-text-muted)]">{d.description}</span>
                                  ) : null}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={submitting || datasetsLoading || hasActiveJob}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                  Queueing…
                </>
              ) : (
                <>
                  <HardDrive className="h-4 w-4 mr-2" aria-hidden />
                  Create export
                </>
              )}
            </Button>
            {hasActiveJob ? (
              <span className="text-meta text-[var(--ds-text-muted)]">An export is already queued or processing.</span>
            ) : null}
          </div>
        </form>

        <div className="mt-10">
          <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-3">Recent exports</h3>
          {jobsLoading && jobs.length === 0 ? (
            <div className="flex items-center gap-2 text-[var(--ds-text-muted)] text-sm py-6">
              <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden />
              Loading history…
            </div>
          ) : jobs.length === 0 ? (
            <p className="text-meta text-[var(--ds-text-muted)] py-4">No exports yet.</p>
          ) : (
            <TooltipProvider>
              <div className="overflow-x-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)]">
                <table className="w-full text-sm text-left min-w-[720px]">
                  <thead className="bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)] text-micro uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 font-medium">Requested</th>
                      <th className="px-3 py-2 font-medium">By</th>
                      <th className="px-3 py-2 font-medium">Datasets</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Completed</th>
                      <th className="px-3 py-2 font-medium">Expires</th>
                      <th className="px-3 py-2 font-medium">Size</th>
                      <th className="px-3 py-2 font-medium w-28"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)]">
                    {jobs.map((job) => {
                      const countsHint = rowCountsHint(job.rowCounts);
                      const canDownload =
                        job.status === 'completed' && !isDownloadExpired(job) && job.storagePath;
                      const requestedLabel = job.requestedAt
                        ? new Date(job.requestedAt).toLocaleString()
                        : '—';
                      const completedLabel = job.completedAt
                        ? new Date(job.completedAt).toLocaleString()
                        : '—';
                      const expiresLabel =
                        job.downloadExpiresAt && job.status === 'completed'
                          ? new Date(job.downloadExpiresAt).toLocaleString()
                          : job.status === 'expired'
                            ? '—'
                            : '—';
                      const who =
                        job.requestedByName ||
                        job.requestedByEmail ||
                        (job.requestedByUserId ? `${String(job.requestedByUserId).slice(0, 8)}…` : '—');
                      const dsArr = Array.isArray(job.datasets) ? job.datasets : [];
                      const dsSummary =
                        dsArr.length > 3
                          ? `${dsArr.slice(0, 3).join(', ')} +${dsArr.length - 3} more`
                          : dsArr.join(', ') || '—';

                      return (
                        <tr key={job.id} className="text-[var(--ds-text-primary)]">
                          <td className="px-3 py-2 whitespace-nowrap text-meta">{requestedLabel}</td>
                          <td className="px-3 py-2 max-w-[140px] truncate" title={who}>
                            {who}
                          </td>
                          <td className="px-3 py-2 max-w-[200px]">
                            <Tooltip
                              content={
                                <span className="text-xs font-mono break-all">{dsArr.join(', ') || '—'}</span>
                              }
                            >
                              <span className="cursor-help underline decoration-dotted truncate block">
                                {dsSummary}
                              </span>
                            </Tooltip>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant={statusBadgeVariant(job.status)}>{job.status}</Badge>
                            {job.status === 'failed' && job.failureReason ? (
                              <p className="text-micro text-[var(--ds-status-danger)] mt-1 max-w-xs">{job.failureReason}</p>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-meta">{completedLabel}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-meta">
                            {job.status === 'completed' && job.downloadExpiresAt ? expiresLabel : '—'}
                            {job.status === 'expired' ? (
                              <span className="block text-micro text-[var(--ds-text-muted)]">File removed</span>
                            ) : null}
                            {isDownloadExpired(job) && job.status === 'completed' ? (
                              <span className="block text-micro text-[var(--ds-text-muted)]">Expired</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {countsHint ? (
                              <Tooltip content={<span className="text-xs max-w-sm">{countsHint}</span>}>
                                <span className="cursor-help underline decoration-dotted">{formatBytes(job.fileSizeBytes)}</span>
                              </Tooltip>
                            ) : (
                              formatBytes(job.fileSizeBytes)
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {canDownload ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                disabled={downloadingId === job.id}
                                onClick={() => handleDownload(job)}
                              >
                                {downloadingId === job.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                ) : (
                                  <Download className="h-4 w-4" aria-hidden />
                                )}
                                ZIP
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
